/**
 * Typed fetch wrapper shared by the admin and public apps. Centralises the
 * base URL, the optional `Authorization: Bearer` header, JSON encoding, and
 * error-envelope parsing so every endpoint call goes through one place.
 *
 * Public (anonymous) callers omit `token`; admin callers pass it.
 */
import { env } from './env'

/** Error carrying the server's machine-readable `error.code` (or
 * `network_error`) so callers/UI can branch without parsing strings.
 * `details` holds any extra keys from the error envelope (e.g. a 422's
 * `field`, a validation 400's `fields`, a 409's `current_updated_at`). */
export class ApiError extends Error {
  code: string
  status: number
  details: Record<string, unknown>
  constructor(
    status: number,
    code: string,
    message: string,
    details: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

export type ApiFetchOptions = {
  token?: string | null
  method?: string
  body?: unknown
  signal?: AbortSignal
  /** Let the request outlive the page (fetch `keepalive`): for a small send
   * fired on the same tap that hands off to the dialler or a new tab. */
  keepalive?: boolean
  /** Extra request headers, e.g. an `Idempotency-Key` (M39-T13). */
  headers?: Record<string, string>
}

export async function apiFetch<T>(
  path: string,
  opts: ApiFetchOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...opts.headers,
  }
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'

  // Build init conditionally — exactOptionalPropertyTypes forbids passing
  // `undefined` for body/signal, so we only set them when present.
  const init: RequestInit = { method: opts.method ?? 'GET', headers }
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body)
  if (opts.signal) init.signal = opts.signal
  if (opts.keepalive) init.keepalive = true

  let resp: Response
  try {
    resp = await fetch(`${env.apiBaseUrl}${path}`, init)
  } catch {
    throw new ApiError(0, 'network_error', 'Could not reach the server')
  }

  if (!resp.ok) {
    let code = 'error'
    let message = 'Request failed'
    let details: Record<string, unknown> = {}
    try {
      const b = (await resp.json()) as {
        error?: Record<string, unknown> & { code?: string; message?: string }
      }
      if (b?.error?.code) code = b.error.code
      if (typeof b?.error?.message === 'string') message = b.error.message
      if (b?.error) {
        details = Object.fromEntries(
          Object.entries(b.error).filter(
            ([k]) => k !== 'code' && k !== 'message',
          ),
        )
      }
    } catch {
      // non-JSON error body — keep the defaults
    }
    throw new ApiError(resp.status, code, message, details)
  }

  return (await resp.json()) as T
}
